import axiosInstance from 'shared/services/api/axiosInstance';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';

const teamHeaders = () => {
  const id = getSelectedTeamNumericId();
  return id != null ? { 'X-Team-Id': String(id) } : {};
};

export async function listDatasets(promptId) {
  const { data } = await axiosInstance.get('/datasets', {
    headers: teamHeaders(),
    params: promptId ? { prompt_id: promptId } : {},
  });
  return data.datasets || [];
}

/**
 * One page of datasets: { datasets, total }. `q` searches names. Each row has
 * item_count, expected_count, sources { sources, live } and can_delete for
 * the caller.
 */
export async function listDatasetsPage({ limit = 25, offset = 0, q = '' } = {}) {
  const { data } = await axiosInstance.get('/datasets', {
    headers: teamHeaders(),
    params: { limit, offset, ...(q ? { q } : {}) },
  });
  return { datasets: data.datasets || [], total: Number(data.total) || 0 };
}

export async function createDataset(name, opts = {}) {
  const { data } = await axiosInstance.post(
    '/datasets',
    { name, description: opts.description, prompt_id: opts.promptId, columns: opts.columns },
    { headers: teamHeaders() },
  );
  return data.dataset;
}

export async function setDatasetColumns(id, columns) {
  const { data } = await axiosInstance.patch(`/datasets/${id}/columns`, { columns }, { headers: teamHeaders() });
  return data.dataset;
}

/**
 * `partial` updates and inserts only the rows in `rows`, and archives
 * `archiveIds`. Rows the grid never loaded stay. Without it, the payload is
 * the whole working set and anything omitted is archived.
 */
export async function replaceRows(id, columns, rows, derivedColumns = null, { partial = false, archiveIds = null } = {}) {
  const body = { columns, rows };
  if (derivedColumns != null) body.derived_columns = derivedColumns;
  if (partial) {
    body.partial = true;
    body.archive_ids = archiveIds || [];
  }
  const { data } = await axiosInstance.put(`/datasets/${id}/rows`, body, { headers: teamHeaders() });
  return data.dataset;
}

/** The dataset header. `summary` omits row bodies; the grid pages those itself. */
export async function getDataset(id, { summary = false } = {}) {
  const { data } = await axiosInstance.get(`/datasets/${id}`, {
    headers: teamHeaders(),
    params: summary ? { summary: 1 } : {},
  });
  return data.dataset;
}

/**
 * One page of rows. `total` is how many match the filters; `item_count` is
 * every active row. The server applies the filters and the order, then LIMIT.
 */
export async function listDatasetItems(id, { limit = 50, offset = 0, filters = [], sort = 'position', dir = 'asc' } = {}) {
  const active = (Array.isArray(filters) ? filters : []).filter((f) => {
    if (!f) return false;
    if (f.operator === 'is_set' || f.operator === 'is_not_set') return true;
    return String(f.value ?? '').trim() !== '';
  });
  const params = { limit, offset, sort, dir };
  if (active.length) {
    params.filters = JSON.stringify(active.map(({ field, operator, value }) => ({ field, operator, value: value ?? '' })));
  }
  const { data } = await axiosInstance.get(`/datasets/${id}/items`, { headers: teamHeaders(), params });
  return {
    items: data.items || [],
    total: Number(data.total) || 0,
    item_count: Number(data.item_count) || 0,
    limit: Number(data.limit) || limit,
    offset: Number(data.offset) || offset,
  };
}

export async function renameDataset(id, name) {
  const { data } = await axiosInstance.patch(`/datasets/${id}`, { name }, { headers: teamHeaders() });
  return data.dataset;
}

/** Name and description. */
export async function updateDataset(id, patch) {
  const { data } = await axiosInstance.patch(`/datasets/${id}`, patch, { headers: teamHeaders() });
  return data.dataset;
}

/** Delete a dataset. */
export async function deleteDataset(id) {
  const { data } = await axiosInstance.delete(`/datasets/${id}`, { headers: teamHeaders() });
  return data;
}

export async function addItems(id, payload) {
  // payload: { items: [...] } | { csv } | { json } | { jsonl }
  //        | { span_ids: [...] } | { run_ids: [...] } | { conversation_ids: [...] } | { eval_run_ids: [...] }
  // Units come back as { items, added, skipped, not_found }.
  const { data } = await axiosInstance.post(`/datasets/${id}/items`, payload, { headers: teamHeaders() });
  return data;
}

/** Archives the item (its id stays resolvable). */
export async function removeItem(id, itemId) {
  const { data } = await axiosInstance.delete(`/datasets/${id}/items/${itemId}`, { headers: teamHeaders() });
  return data;
}

/** A MERGE: only the keys in `patch` change on the server. */
export async function updateItem(id, itemId, patch) {
  const { data } = await axiosInstance.patch(`/datasets/${id}/items/${itemId}`, patch, { headers: teamHeaders() });
  return data.dataset;
}

/** Re-snapshot the Evals v3 verdicts onto rows: one item, some (item_ids), or every unit row. */
export async function refreshEvaluations(id, { itemId = null, itemIds = null } = {}) {
  const url = itemId ? `/datasets/${id}/items/${itemId}/refresh-evaluations` : `/datasets/${id}/items/refresh-evaluations`;
  const { data } = await axiosInstance.post(url, itemIds ? { item_ids: itemIds } : {}, { headers: teamHeaders() });
  return data; // { refreshed, dataset }
}

/** Which unit rows point at a span / run / conversation that retention has swept: { checked, expired: [itemId] }. */
/** Expired unit rows. `itemIds` limits the check to the page on screen. */
export async function getItemSources(id, { itemIds = null } = {}) {
  const params = {};
  if (Array.isArray(itemIds) && itemIds.length) params.item_ids = itemIds.join(',');
  const { data } = await axiosInstance.get(`/datasets/${id}/items/sources`, { headers: teamHeaders(), params });
  return data;
}

// ── sources: saved filters that fill the dataset, once or live ──────────────

/** How many units a source filter matches: { matched, capped, window, sample }. spec = { kind, filter, options }. */
export async function previewSource(spec) {
  const { data } = await axiosInstance.post('/datasets/sources/preview', spec, { headers: teamHeaders() });
  return data;
}

export async function listSources(id) {
  const { data } = await axiosInstance.get(`/datasets/${id}/sources`, { headers: teamHeaders() });
  return data.sources || [];
}

/** Add a source and run it once now: { source, feed }. spec = { kind, filter, options?, live? }. */
export async function createSource(id, spec) {
  const { data } = await axiosInstance.post(`/datasets/${id}/sources`, spec, { headers: teamHeaders() });
  return data;
}

/** Change a source: { filter?, options?, live?, paused? } → { source, feed? }. */
export async function updateSource(id, sourceId, patch) {
  const { data } = await axiosInstance.patch(`/datasets/${id}/sources/${sourceId}`, patch, { headers: teamHeaders() });
  return data;
}

/** Run a source again. No body: over its own window ("Run now"). { time, filter? }: an Update. */
export async function runSource(id, sourceId, body = {}) {
  const { data } = await axiosInstance.post(`/datasets/${id}/sources/${sourceId}/run`, body, { headers: teamHeaders() });
  return data;
}

export async function listSourceRuns(id, sourceId) {
  const { data } = await axiosInstance.get(`/datasets/${id}/sources/${sourceId}/runs`, { headers: teamHeaders() });
  return data.runs || [];
}

export async function removeSource(id, sourceId) {
  const { data } = await axiosInstance.delete(`/datasets/${id}/sources/${sourceId}`, { headers: teamHeaders() });
  return data;
}

// ── export: JSONL, tokens and the audit ─────────────────────────────────────

/**
 * The export as text (JSONL). opts: { rows: 'all' | 'expected', version, since, ids }.
 * Returns { text, rows, version } where rows is the server's count header.
 */
export async function exportDataset(id, { rows = 'all', version = null, since = null, ids = null, format = null, target = null } = {}) {
  const params = { rows };
  if (format && format !== 'trodo') params.format = format;
  if (target && format && format !== 'trodo') params.target = target;
  if (version != null) params.version = version;
  if (since) params.since = since;
  if (Array.isArray(ids) && ids.length) params.ids = ids.join(',');
  const res = await axiosInstance.get(`/datasets/${id}/export`, { headers: teamHeaders(), params, responseType: 'text', transformResponse: [(d) => d] });
  return {
    text: typeof res.data === 'string' ? res.data : JSON.stringify(res.data),
    rows: Number(res.headers?.['x-trodo-export-rows']) || 0,
    skipped: Number(res.headers?.['x-trodo-export-skipped']) || 0,
    version: res.headers?.['x-trodo-dataset-version'] || null,
  };
}

export async function listExportTokens() {
  const { data } = await axiosInstance.get('/datasets/export-tokens', { headers: teamHeaders() });
  return data.tokens || [];
}

/** Mint a token; the returned object's `token` is the secret, shown once. */
export async function createExportToken({ label, datasetIds = null }) {
  const { data } = await axiosInstance.post('/datasets/export-tokens', { label, dataset_ids: datasetIds }, { headers: teamHeaders() });
  return data.token;
}

export async function revokeExportToken(id) {
  const { data } = await axiosInstance.delete(`/datasets/export-tokens/${id}`, { headers: teamHeaders() });
  return data.token;
}

export async function listExportAudit({ datasetId = null, limit = 50 } = {}) {
  const { data } = await axiosInstance.get('/datasets/export-audit', { headers: teamHeaders(), params: { ...(datasetId ? { dataset_id: datasetId } : {}), limit } });
  return data.pulls || [];
}

/** The datasets that already hold a unit: [{dataset_id, dataset_name, item_id}]. */
export async function datasetsForUnit({ spanId, runId, conversationId }) {
  const params = {};
  if (spanId) params.span_id = spanId; else if (runId) params.run_id = runId; else if (conversationId) params.conversation_id = conversationId;
  const { data } = await axiosInstance.get('/datasets/unit', { headers: teamHeaders(), params });
  return data.datasets || [];
}

/** A version's rows become the dataset's again (itself a new version). → { dataset, version } */
export async function restoreDatasetVersion(id, versionNo) {
  const { data } = await axiosInstance.post(`/datasets/${id}/versions/${versionNo}/restore`, {}, { headers: teamHeaders() });
  return data;
}

export async function listDatasetVersions(id) {
  const { data } = await axiosInstance.get(`/datasets/${id}/versions`, { headers: teamHeaders() });
  return data.versions || [];
}

export async function saveDatasetVersion(id, payload) {
  const { data } = await axiosInstance.post(`/datasets/${id}/versions`, payload, { headers: teamHeaders() });
  return data.version;
}
